// app/src/lib/cashflow/ar.js — Accounts Receivable tracking (US-3.3).
//
// MVP is manual-entry. AR is matched against incoming Plaid transactions using
// the same fuzzy strategy as invoice reconciliation: customer name + amount ±$1
// + date window around expectedDate.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { cents, daysBetween, isoToday } from './util.js';
import { mutate } from '../scale/instrument.js';

/** @typedef {{ arId:string, userId:string, customerName:string, normalizedName:string, invoiceNumber?:string, amountCents:number, invoiceDate:string, expectedDate:string, status:'OPEN'|'RECEIVED'|'OVERDUE'|'WRITTEN_OFF', plaidTxId?:string, notes?:string, createdAt:number }} ArEntry */

const AMOUNT_TOLERANCE_CENTS = 100;
const DATE_BEFORE_EXPECTED_DAYS = 3;
const DATE_AFTER_EXPECTED_DAYS = 7;

export async function create(input) {
  const userId = await requireUserId();
  if (!input.customerName) throw new Error('ar.create: customerName required');
  if (!Number.isFinite(input.amount) || input.amount <= 0) throw new Error('ar.create: amount must be > 0');
  /** @type {ArEntry} */
  const row = {
    arId: input.arId || crypto.randomUUID(),
    userId,
    customerName: input.customerName,
    normalizedName: normalize(input.customerName),
    invoiceNumber: input.invoiceNumber,
    amountCents: cents(input.amount),
    invoiceDate: input.invoiceDate || isoToday(),
    expectedDate: input.expectedDate || input.invoiceDate || isoToday(),
    status: 'OPEN',
    notes: input.notes,
    createdAt: Date.now(),
  };
  return mutate(
    { entityType: 'arEntry', entityId: row.arId, action: 'create', after: row },
    async () => { await db.arEntries.put(row); return row; },
  );
}

export async function update(arId, patch) {
  const userId = await requireUserId();
  const existing = await db.arEntries.get(arId);
  if (!existing || existing.userId !== userId) throw new Error('ar.update: not found');
  const next = {
    ...existing,
    ...patch,
    amountCents: patch.amount != null ? cents(patch.amount) : existing.amountCents,
    normalizedName: patch.customerName ? normalize(patch.customerName) : existing.normalizedName,
  };
  return mutate(
    { entityType: 'arEntry', entityId: arId, action: 'update', before: existing, after: next },
    async () => { await db.arEntries.put(next); return next; },
  );
}

export async function remove(arId) {
  const userId = await requireUserId();
  const existing = await db.arEntries.get(arId);
  if (!existing || existing.userId !== userId) return;
  await mutate(
    { entityType: 'arEntry', entityId: arId, action: 'delete', before: existing },
    async () => { await db.arEntries.delete(arId); },
  );
}

export async function listOpen() {
  const userId = await requireUserId();
  return db.arEntries.where('[userId+status]').anyOf([[userId, 'OPEN'], [userId, 'OVERDUE']]).toArray();
}

/**
 * Build AR aging buckets (same shape as AP aging) keyed by days past expectedDate.
 */
export async function buildAging() {
  const userId = await requireUserId();
  const today = isoToday();
  const open = await listOpen();
  const buckets = [
    { id: 'current',  label: 'Current',        rows: [], totalCents: 0 },
    { id: 'd1_7',     label: '1–7 days',       rows: [], totalCents: 0 },
    { id: 'd8_14',    label: '8–14 days',      rows: [], totalCents: 0 },
    { id: 'd15_30',   label: '15–30 days',     rows: [], totalCents: 0 },
    { id: 'over30',   label: 'Over 30 days',   rows: [], totalCents: 0 },
  ];
  let total = 0;
  for (const ar of open) {
    const overdue = daysBetween(today, ar.expectedDate);
    let b;
    if (overdue <= 0) b = buckets[0];
    else if (overdue <= 7) b = buckets[1];
    else if (overdue <= 14) b = buckets[2];
    else if (overdue <= 30) b = buckets[3];
    else b = buckets[4];
    b.rows.push({ ...ar, daysOverdue: Math.max(0, overdue) });
    b.totalCents += ar.amountCents;
    total += ar.amountCents;
  }
  return { asOf: today, buckets, totalCents: total };
}

/**
 * Run after a WAL merge: for each open AR, look for a matching cleared
 * inflow Plaid transaction (negative amountCents == money in).
 *
 * @returns {Promise<{ matched: number }>}
 */
export async function reconcileFromPlaid() {
  const userId = await requireUserId();
  const open = await listOpen();
  if (!open.length) return { matched: 0 };

  // Pull a reasonable window of cleared inflow transactions.
  const today = isoToday();
  const lookback = new Date(today + 'T00:00:00Z'); lookback.setUTCDate(lookback.getUTCDate() - 30);
  const lookbackIso = lookback.toISOString().slice(0, 10);

  const txs = await db.plaidTransactions
    .where('[userId+date]').between([userId, lookbackIso], [userId, today], true, true)
    .filter((t) => !t.pending && t.amountCents < 0) // inflow only
    .toArray();

  let matched = 0;
  // Track which Plaid tx IDs we've already consumed in this run so two open AR
  // entries cannot both claim the same payment.
  const usedTxIds = new Set();
  // Also exclude any tx already referenced by another AR entry (any status).
  const allArs = await db.arEntries.where('userId').equals(userId).toArray();
  for (const a of allArs) if (a.plaidTxId) usedTxIds.add(a.plaidTxId);

  for (const ar of open) {
    if (ar.plaidTxId) continue;
    const target = ar.amountCents;
    const arNorm = ar.normalizedName;
    const winStart = (() => { const d = new Date(ar.expectedDate); d.setUTCDate(d.getUTCDate() - DATE_BEFORE_EXPECTED_DAYS); return d.toISOString().slice(0,10); })();
    const winEnd   = (() => { const d = new Date(ar.expectedDate); d.setUTCDate(d.getUTCDate() + DATE_AFTER_EXPECTED_DAYS); return d.toISOString().slice(0,10); })();
    const candidate = txs.find((t) => {
      if (usedTxIds.has(t.plaidTxId)) return false;
      if (t.date < winStart || t.date > winEnd) return false;
      const amtIn = -t.amountCents;
      if (Math.abs(amtIn - target) > AMOUNT_TOLERANCE_CENTS) return false;
      const txNorm = normalize(t.merchantName || '');
      if (!txNorm) return false;
      return arNorm.includes(txNorm) || txNorm.includes(arNorm);
    });
    if (candidate) {
      ar.status = 'RECEIVED';
      ar.plaidTxId = candidate.plaidTxId;
      await db.arEntries.put(ar);
      usedTxIds.add(candidate.plaidTxId);
      matched += 1;
    }
  }
  return { matched };
}

function normalize(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
